import React, { useMemo, useRef, useState } from 'react';
import { ArrowRight, Check, FileText, PlugZap, Sparkles, Upload, X } from 'lucide-react';
import { SupportedCurrency, Transaction } from '../types';
import { BatchInstrumentCandidate, InstrumentSearchResult } from '../types/instruments';
import { parseCsv } from '../importers';
import { smartParseTransactions, AiCsvMapping } from '../utils/aiCsvImporter';
import { resolveInstrument, resolveInstrumentsBatch, upsertInstrumentQuoteUnit } from '../services/instrumentService';
import { brokerGuideOrder, brokerImportGuides, BrokerId } from '../config/brokerImportGuides';
import BrokerSelector from './import/BrokerSelector';
import BrokerInstructionsModal from './import/BrokerInstructionsModal';

interface PortfolioUploadModalProps {
    open: boolean;
    onClose: () => void;
    onImport: (transactions: Transaction[], sourceLabel: string) => void;
    defaultCurrency: SupportedCurrency;
}

interface UniqueInstrumentCandidate {
    key: string;
    isin?: string;
    displaySymbol: string;
    exchange?: string;
    currency: string;
    priceCurrency?: string;
    totalCurrency?: string;
}

interface AmbiguousInstrumentResolution {
    key: string;
    isin?: string;
    displaySymbol: string;
    exchange?: string;
    currency: string;
    priceCurrency?: string;
    totalCurrency?: string;
    matches: InstrumentSearchResult[];
}

const preferredExchangesByCurrency: Record<string, string[]> = {
    USD: ['US', 'NASDAQ', 'NYSE', 'ARCA'],
    GBP: ['LSE', 'XLON', 'LON'],
    GBX: ['LSE', 'XLON', 'LON'],
    EUR: ['XETRA', 'FRA', 'PAR', 'MIL', 'AMS'],
    CAD: ['TO', 'TSX', 'CNQ', 'CSE'],
    AUD: ['ASX'],
};

const scoreInstrumentMatch = (candidate: UniqueInstrumentCandidate, match: InstrumentSearchResult) => {
    let score = 0;
    const matchCurrency = (match.currency || '').trim().toUpperCase();
    const candidateCurrency = (candidate.currency || '').trim().toUpperCase();
    const matchExchange = (match.exchange || '').trim().toUpperCase();
    const candidateExchange = (candidate.exchange || '').trim().toUpperCase();

    if (candidateExchange && matchExchange === candidateExchange) score += 100;
    if (candidateCurrency && matchCurrency === candidateCurrency) score += 40;

    const preferredExchanges = preferredExchangesByCurrency[candidateCurrency] || [];
    if (preferredExchanges.some((exchange) => matchExchange.includes(exchange))) score += 20;

    if (candidate.isin && match.isin && candidate.isin === match.isin.trim().toUpperCase()) score += 200;

    return score;
};

const importMethods = [
    {
        key: 'connect',
        title: 'Connect to your brokerage',
        description: 'Sync automatically via secure aggregators. Bank-grade encryption.',
        icon: <PlugZap size={18} />,
        disabled: true,
    },
    {
        key: 'manual',
        title: 'Enter your holdings',
        description: 'Quickly input tickers and quantities for a lightweight setup.',
        icon: <Check size={18} />,
        disabled: true,
    },
    {
        key: 'statement',
        title: 'Upload brokerage statement',
        description: 'Import transactions from any CSV. T212 uses native parser. Others use AI mapping.',
        icon: <Upload size={18} />,
        disabled: false,
    },
];

const normalizeDisplaySymbol = (tx: Transaction) =>
    (tx.displaySymbol || tx.symbol || '').trim().toUpperCase();


const inferExchangeFromTransaction = (tx: Transaction): string | undefined => {
    const exchange = tx.exchange?.trim().toUpperCase();
    if (exchange) return exchange;

    const priceCurrency = (tx.originalPriceCurrency || tx.priceCurrency || tx.currency || '').trim().toUpperCase();
    if (priceCurrency === 'GBX' || priceCurrency === 'GBP') {
        return 'LSE';
    }

    return undefined;
};

const instrumentKeyForTransaction = (tx: Transaction) => {
    const displaySymbol = normalizeDisplaySymbol(tx);
    const exchange = (tx.exchange || '').trim().toUpperCase();
    // Prefer listing-qualified keys when available so instruments that share
    // an ISIN (for example, dual-currency ETF lines) do not collapse into one.
    if (displaySymbol && exchange) return `symex:${exchange}:${displaySymbol}`;
    const isin = tx.isin?.trim().toUpperCase();
    if (isin) return `isin:${isin}`;
    const currency = (tx.priceCurrency || tx.currency || '').trim().toUpperCase();
    return `symbol:${displaySymbol}|currency:${currency}`;
};

const describeUploadError = (error: unknown): string => {
    if (error instanceof Error) {
        return `Upload failed: ${error.message}`;
    }

    return 'We could not read this file. Please try again.';
};

export default function PortfolioUploadModal({ open, onClose, onImport }: PortfolioUploadModalProps) {
    const [selectedMethod, setSelectedMethod] = useState<string>('statement');
    const [selectedBrokerId, setSelectedBrokerId] = useState<BrokerId>('trading212');
    const [instructionsOpen, setInstructionsOpen] = useState(false);
    const [statusMessage, setStatusMessage] = useState<string>('');
    const [mapping, setMapping] = useState<AiCsvMapping | null>(null);
    const [isParsing, setIsParsing] = useState(false);
    const [isResolvingImport, setIsResolvingImport] = useState(false);
    const [needsResolution, setNeedsResolution] = useState<AmbiguousInstrumentResolution[]>([]);
    const [selectedMatchesByKey, setSelectedMatchesByKey] = useState<Record<string, string>>({});
    const [pendingImport, setPendingImport] = useState<{ transactions: Transaction[]; sourceLabel: string } | null>(null);

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const brokerGrid = useMemo(() => brokerGuideOrder, []);
    const selectedGuide = brokerImportGuides[selectedBrokerId];

    const closeModalAfterImport = () => {
        setStatusMessage('');
        setMapping(null);
        setPendingImport(null);
        setNeedsResolution([]);
        setSelectedMatchesByKey({});
        setInstructionsOpen(false);
        onClose();
    };

    const persistTrading212QuoteUnits = async (
        parsed: Transaction[],
        resolvedByKey: Record<string, string>
    ) => {
        const gbxInstrumentIds = new Set<string>();
        parsed.forEach((tx) => {
            const originalPriceCurrency = (tx.originalPriceCurrency || '').trim().toUpperCase();
            if (originalPriceCurrency !== 'GBX') return;
            const key = instrumentKeyForTransaction(tx);
            const instrumentId = resolvedByKey[key];
            if (!instrumentId) return;
            gbxInstrumentIds.add(instrumentId);
        });

        await Promise.all(
            Array.from(gbxInstrumentIds).map((instrumentId) =>
                upsertInstrumentQuoteUnit(instrumentId, 'GBX', 0.01, 'GBP')
            )
        );
    };

    const finalizeTrading212Import = async (
        parsed: Transaction[],
        sourceLabel: string,
        resolvedByKey: Record<string, string>,
        unresolvedKeys: Set<string>
    ) => {
        let quoteUnitWarning: string | null = null;
        try {
            await persistTrading212QuoteUnits(parsed, resolvedByKey);
        } catch (error) {
            console.error('Failed to persist Trading 212 quote units:', error);
            quoteUnitWarning = describeUploadError(error);
        }

        const updated = parsed.map((tx) => {
            const key = instrumentKeyForTransaction(tx);
            const instrumentId = resolvedByKey[key];
            const unresolved = unresolvedKeys.has(key);
            return {
                ...tx,
                displaySymbol: normalizeDisplaySymbol(tx),
                instrumentId: instrumentId || undefined,
                exchange: tx.exchange?.trim().toUpperCase() || inferExchangeFromTransaction(tx),
                unresolved,
            };
        });

        const unresolvedCount = updated.filter((tx) => tx.unresolved).length;
        if (unresolvedCount > 0) {
            setStatusMessage(
                `${quoteUnitWarning ? `${quoteUnitWarning} ` : ''}Parsed ${updated.length} transactions. ${unresolvedCount} transaction(s) are unresolved and can be fixed later.`
            );
        } else {
            setStatusMessage(`${quoteUnitWarning ? `${quoteUnitWarning} ` : ''}Parsed ${updated.length} transactions using Trading 212 template.`);
        }

        onImport(updated, sourceLabel);
        closeModalAfterImport();
    };

    const resolveTrading212Instruments = async (parsed: Transaction[], sourceLabel: string) => {
        const uniqueCandidates = new Map<string, UniqueInstrumentCandidate>();
        parsed.forEach((tx) => {
            const displaySymbol = normalizeDisplaySymbol(tx);
            const isin = tx.isin?.trim().toUpperCase();
            if (!displaySymbol || displaySymbol === 'CASH') return;
            const key = instrumentKeyForTransaction(tx);
            if (uniqueCandidates.has(key)) return;
            uniqueCandidates.set(key, {
                key,
                isin,
                displaySymbol,
                exchange: inferExchangeFromTransaction(tx),
                currency: (tx.priceCurrency || tx.currency || '').trim().toUpperCase(),
                priceCurrency: (tx.originalPriceCurrency || tx.priceCurrency || '').trim().toUpperCase(),
                totalCurrency: (tx.totalCurrency || tx.currency || '').trim().toUpperCase(),
            });
        });

        const resolvedByKey: Record<string, string> = {};
        const unresolvedKeys = new Set<string>();
        const ambiguous: AmbiguousInstrumentResolution[] = [];

        const batchCandidates: BatchInstrumentCandidate[] = Array.from(uniqueCandidates.values()).map((item) => ({
            key: item.key,
            symbol: item.displaySymbol,
            isin: item.isin,
            exchange: item.exchange,
            currency: item.currency,
            priceCurrency: item.priceCurrency,
            totalCurrency: item.totalCurrency,
        }));

        const batchResults = await resolveInstrumentsBatch(batchCandidates);
        batchResults.forEach((result) => {
            if (result.status === 'resolved' && result.instrument?.instrumentId) {
                resolvedByKey[result.key] = result.instrument.instrumentId;
            } else if (result.status === 'needsChoice' && result.matches?.length) {
                const base = uniqueCandidates.get(result.key);
                if (base) {
                    const rankedMatches = [...result.matches].sort((a, b) => scoreInstrumentMatch(base, b) - scoreInstrumentMatch(base, a));
                    ambiguous.push({ ...base, matches: rankedMatches });
                }
            } else {
                unresolvedKeys.add(result.key);
            }
        });

        uniqueCandidates.forEach((_item, key) => {
            if (!resolvedByKey[key] && !ambiguous.find((a) => a.key === key)) {
                unresolvedKeys.add(key);
            }
        });

        if (ambiguous.length > 0) {
            const defaults: Record<string, string> = {};
            ambiguous.forEach((item) => {
                const first = item.matches[0];
                defaults[item.key] = first ? `${first.source}:${first.providerSymbol}` : '';
            });
            setSelectedMatchesByKey(defaults);
            setNeedsResolution(ambiguous);
            setPendingImport({ transactions: parsed, sourceLabel });
            setStatusMessage(`Parsed ${parsed.length} transactions. ${ambiguous.length} instruments need resolution.`);
            return;
        }

        await finalizeTrading212Import(parsed, sourceLabel, resolvedByKey, unresolvedKeys);
    };

    const handleConfirmAmbiguousResolution = async () => {
        if (!pendingImport) return;

        setIsResolvingImport(true);

        const resolvedByKey: Record<string, string> = {};
        const unresolvedKeys = new Set<string>();

        for (const item of needsResolution) {
            const selectedOption = selectedMatchesByKey[item.key];
            const [selectedSource, selectedProviderSymbol] = selectedOption.split(':');
            const selected = item.matches.find((match) => match.providerSymbol === selectedProviderSymbol && match.source === selectedSource);
            if (!selected) {
                unresolvedKeys.add(item.key);
                continue;
            }

            try {
                const resolved = await resolveInstrument({
                    source: selected.source,
                    providerSymbol: selected.providerSymbol,
                    symbol: item.displaySymbol,
                    isin: item.isin,
                    currency: item.currency,
                    priceCurrency: item.priceCurrency,
                    totalCurrency: item.totalCurrency,
                    exchange: selected.exchange || item.exchange,
                });
                if (resolved.instrumentId) {
                    resolvedByKey[item.key] = resolved.instrumentId;
                } else {
                    unresolvedKeys.add(item.key);
                }
            } catch {
                unresolvedKeys.add(item.key);
            }
        }
        try {
            await finalizeTrading212Import(pendingImport.transactions, pendingImport.sourceLabel, resolvedByKey, unresolvedKeys);
        } finally {
            setIsResolvingImport(false);
        }
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setStatusMessage('Reading file...');
        setIsParsing(true);

        try {
            const text = await file.text();
            let parsed: Transaction[] = [];
            if (selectedBrokerId === 'trading212') {
                parsed = parseCsv('trading212', text);
                if (parsed.length > 0) {
                    await resolveTrading212Instruments(parsed, `${selectedGuide.name} CSV`);
                }
            } else {
                const heuristicallyParsed = parseCsv(selectedBrokerId, text);
                const needsReview = heuristicallyParsed.some((tx) => tx.reviewRequired);

                if (!needsReview && heuristicallyParsed.length > 0) {
                    parsed = heuristicallyParsed;
                    setMapping(null);
                    setStatusMessage(`Parsed ${parsed.length} transactions using heuristic mapping.`);
                } else {
                    const { transactions, mapping: detectedMapping } = await smartParseTransactions(text, selectedGuide.name);
                    parsed = transactions;
                    setMapping(detectedMapping);
                    setStatusMessage(
                        parsed.length > 0
                            ? detectedMapping
                                ? `AI mapped columns for ${selectedGuide.name} and parsed ${parsed.length} transactions.`
                                : needsReview
                                  ? `Parsed ${parsed.length} transactions, but column mapping is ambiguous. Review required.`
                                  : `Parsed ${parsed.length} transactions using our default import template.`
                            : 'AI could not confidently map this file. Please check the CSV headers.'
                    );
                }

                if (parsed.length > 0) {
                    onImport(parsed, `${selectedGuide.name} CSV`);
                    closeModalAfterImport();
                }
            }
        } catch (error) {
            console.error(error);
            setStatusMessage(describeUploadError(error));
        } finally {
            setIsParsing(false);
            event.target.value = '';
        }
    };

    const handleBrokerSelect = (brokerId: BrokerId) => {
        setSelectedBrokerId(brokerId);
        setInstructionsOpen(true);
    };

    const handleInstructionsUpload = () => {
        setInstructionsOpen(false);
        fileInputRef.current?.click();
    };

    if (!open) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
                <div className="bg-[#0f0f14] border border-white/10 rounded-2xl shadow-2xl w-full max-w-5xl mt-8">
                    <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
                        <div>
                            <p className="text-xs uppercase tracking-[0.2em] text-indigo-400">Add Portfolio</p>
                            <h3 className="text-lg font-semibold text-white">Upload brokerage statement</h3>
                        </div>
                        <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors" aria-label="Close modal">
                            <X size={18} />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
                        <div className="lg:col-span-1 space-y-3">
                            {importMethods.map((method) => (
                                <button
                                    key={method.key}
                                    disabled={method.disabled}
                                    onClick={() => setSelectedMethod(method.key)}
                                    className={`w-full text-left p-4 rounded-xl border flex items-center gap-3 transition-all ${
                                        selectedMethod === method.key ? 'border-indigo-500/50 bg-indigo-500/10' : 'border-white/5 bg-[#12121a] hover:border-indigo-500/30'
                                    } ${method.disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                                >
                                    <div className="p-2 rounded-lg bg-indigo-500/20 text-indigo-300">{method.icon}</div>
                                    <div>
                                        <p className="text-white font-semibold">{method.title}</p>
                                        <p className="text-xs text-slate-400">{method.description}</p>
                                    </div>
                                </button>
                            ))}
                        </div>

                        <div className="lg:col-span-2 rounded-2xl border border-white/5 bg-[#12121a] p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <p className="text-xs uppercase tracking-[0.16em] text-indigo-300">Step 1</p>
                                    <h4 className="text-xl font-semibold text-white">Select your broker</h4>
                                </div>
                                <FileText className="text-indigo-400" size={18} />
                            </div>

                            <BrokerSelector brokerIds={brokerGrid} guides={brokerImportGuides} selectedBrokerId={selectedBrokerId} onSelect={handleBrokerSelect} />

                            <div className="border border-dashed border-white/10 rounded-xl p-6 flex flex-col items-center justify-center transition-colors bg-[#0f0f14]">
                                <div className="w-12 h-12 rounded-full bg-indigo-500/10 text-indigo-400 flex items-center justify-center mb-3">
                                    <Upload size={24} />
                                </div>
                                <p className="text-white font-semibold">Choose a broker to continue</p>
                                <p className="text-xs text-slate-500 text-center max-w-lg">
                                    Trading 212 has native import support today. Other brokers are currently supported through our generic importer while we add
                                    dedicated integrations. If needed, you can also add holdings manually.
                                </p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept={selectedGuide.accepts.join(',')}
                                    className="hidden"
                                    onChange={handleFileChange}
                                />
                            </div>

                            {statusMessage && (
                                <div className="mt-4 p-3 rounded-lg bg-[#0c0c12] border border-white/10 text-sm text-slate-200 flex items-start gap-2">
                                    <ArrowRight size={16} className="text-indigo-400 mt-0.5" />
                                    <div>
                                        <p>{statusMessage}</p>
                                        {mapping && (
                                            <p className="text-xs text-slate-400 mt-1">
                                                Mapping used: {Object.entries(mapping)
                                                    .filter(([, value]) => Boolean(value))
                                                    .map(([key, value]) => `${key} → ${value}`)
                                                    .join(', ')}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {isParsing && (
                                <div className="mt-3 text-xs text-slate-500 flex items-center gap-2">
                                    <Sparkles size={14} className="animate-spin" />
                                    <span>Parsing your file...</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {needsResolution.length > 0 && (
                <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4">
                    <div className="w-full max-w-3xl rounded-2xl border border-white/10 bg-[#12121a] p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-white">Resolve ambiguous instruments</h3>
                            <button
                                onClick={() => {
                                    if (isResolvingImport) return;
                                    setNeedsResolution([]);
                                    setPendingImport(null);
                                    setSelectedMatchesByKey({});
                                }}
                                className="p-2 rounded-lg hover:bg-white/10 text-slate-300 disabled:opacity-60 disabled:cursor-not-allowed"
                                disabled={isResolvingImport}
                            >
                                <X size={16} />
                            </button>
                        </div>
                        <p className="text-sm text-slate-300 mb-4">Select one match for each instrument before completing import.</p>

                        <div className="space-y-3 max-h-[45vh] overflow-auto pr-1">
                            {needsResolution.map((item) => (
                                <div key={item.key} className="rounded-lg border border-white/10 bg-[#0f0f14] p-3">
                                    <p className="text-xs text-slate-400 mb-2">
                                        ISIN: {item.isin || 'N/A'} • Symbol: {item.displaySymbol} • Currency: {item.currency || 'N/A'}
                                    </p>
                                    <select
                                        value={selectedMatchesByKey[item.key] || ''}
                                        onChange={(e) =>
                                            setSelectedMatchesByKey((prev) => ({
                                                ...prev,
                                                [item.key]: e.target.value,
                                            }))
                                        }
                                        className="w-full rounded-lg border border-white/10 bg-[#0c0c12] px-3 py-2 text-sm text-white"
                                        disabled={isResolvingImport}
                                    >
                                        {item.matches.map((match) => (
                                            <option key={`${match.source}:${match.providerSymbol}`} value={`${match.source}:${match.providerSymbol}`}>
                                                {match.ticker} • {match.name} • {match.exchange}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            ))}
                        </div>

                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                onClick={() => {
                                    if (isResolvingImport) return;
                                    setNeedsResolution([]);
                                    setPendingImport(null);
                                    setSelectedMatchesByKey({});
                                }}
                                className="px-4 py-2 rounded-lg border border-white/10 text-slate-300 disabled:opacity-60 disabled:cursor-not-allowed"
                                disabled={isResolvingImport}
                            >
                                Cancel import
                            </button>
                            <button
                                onClick={handleConfirmAmbiguousResolution}
                                className="px-4 py-2 rounded-lg bg-indigo-600 text-white disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                                disabled={isResolvingImport}
                            >
                                {isResolvingImport && <Sparkles size={14} className="animate-spin" />}
                                {isResolvingImport ? 'Importing your data...' : 'Resolve and import'}
                            </button>
                        </div>

                        {isResolvingImport && (
                            <div className="mt-3 text-xs text-slate-400 flex items-center gap-2">
                                <Sparkles size={14} className="animate-spin text-indigo-300" />
                                <span>Importing your data in the background. Please wait and do not refresh this page.</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <BrokerInstructionsModal
                open={instructionsOpen}
                guide={selectedGuide}
                onClose={() => setInstructionsOpen(false)}
                onBack={() => setInstructionsOpen(false)}
                onUpload={handleInstructionsUpload}
            />
        </>
    );
}
